import { farmDayKey } from './farmTime'
import { readFeedRules, type LatestValue } from './feedRules'
import { withUnitPlans } from './unitPlans'

// Findings #217: the farm measures temperature and oxygen at one point per area
// (ARFISH, 2026-10-09), so most units have no reading of their own. The office
// sets per unit where its water is measured (`ProductionUnit.metadata.measuringPointId`,
// another unit of the facility; no migration, phones not yet updated ignore it).
//
// A unit's reading of today wins; otherwise the measuring point's reading of
// today; with neither from today, the newer of the two. Used by the stop rules
// (#211), "O2 alcanza" and the ration temperature in Alimento → Toda la
// instalación, and as TUA's second fallback after the same arrival (#150).

type WithMetadata = { id?: string; metadata?: Record<string, unknown> | null } | null | undefined

export const readMeasuringPointId = (unit: WithMetadata): string | null => {
  const metadata = unit?.metadata
  const raw = metadata && typeof metadata === 'object' ? metadata.measuringPointId : undefined
  return typeof raw === 'string' && raw.trim() && raw !== unit?.id ? raw : null
}

export const withMeasuringPoint = <T extends NonNullable<WithMetadata>>(unit: T, pointId: string | null): T => {
  const metadata = { ...(unit.metadata ?? {}) }
  if (pointId && pointId !== unit.id) metadata.measuringPointId = pointId
  else delete metadata.measuringPointId
  return { ...unit, metadata }
}

/** The unit whose readings this unit uses, among `units` (null when unset or no longer there). */
export const resolveMeasuringPoint = <U extends { id: string; name?: string | null }>(
  unit: WithMetadata,
  units: ReadonlyArray<U>
): U | null => {
  const pointId = readMeasuringPointId(unit)
  return pointId ? units.find((candidate) => candidate.id === pointId) ?? null : null
}

export type PointReading = LatestValue & { fromUnit?: string }

/** The unit's own reading or its measuring point's, by the rule above. */
export const pickReading = (args: {
  own: LatestValue | null
  point: LatestValue | null
  pointName: string | null | undefined
  day: string
  timeZone: string
}): PointReading | null => {
  const { own, point, day, timeZone } = args
  const fromPoint = (value: LatestValue): PointReading => ({ ...value, fromUnit: args.pointName || undefined })
  const isToday = (value: LatestValue | null) => Boolean(value && farmDayKey(value.at, timeZone) === day)
  if (isToday(own)) return own
  if (point && isToday(point)) return fromPoint(point)
  if (!point) return own
  if (!own || point.at > own.at) return fromPoint(point)
  return own
}

/**
 * "Copiar solo reglas y punto de medición": the source's stop rules and oxygen
 * settings (none = removed), and optionally its measuring point — the source
 * itself when it has none (a unit is never its own point). Nothing else changes.
 */
export const copyRulesAndMeasuringPoint = <T extends NonNullable<WithMetadata>>(
  source: WithMetadata,
  target: T,
  options: { copyPoint: boolean }
): T => {
  const withRules = withUnitPlans(target, { feedRules: readFeedRules(source) })
  if (!options.copyPoint) return withRules
  return withMeasuringPoint(withRules, readMeasuringPointId(source) ?? source?.id ?? null)
}
